import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import CustomInput from '../custominput/CustomInput';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@material-ui/core';
import EventLabel from '../EventLabel/EventLabel';
import { useStore } from '../../store/Store';

const GROUP = 'CMS.EmailLinkGate';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Asks for the visitor's email and mails their personal link. The answer is the same
// whether or not the email is registered, so the page never reveals who is.
export const EmailLinkGate = observer(() => {
  const store = useStore();
  // CustomInput also uses the label as the input's aria-label and name, so it must be plain text.
  // The group is loaded by the EventLabels below; until then the default shows.
  const inputLabel = store.eventLabels?.[GROUP.toUpperCase()]?.EMAILLINKGATEINPUTLABEL?.value ?? 'Email address';
  const [email, setEmail] = useState('');
  const [invalid, setInvalid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async () => {
    if (loading) return;
    const value = email.trim();
    if (!EMAIL_PATTERN.test(value)) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    setLoading(true);
    try {
      await store.requestEmailLink(value);
    } catch {
      // Same message either way.
    } finally {
      setLoading(false);
      setSent(true);
    }
  };

  const onKeyDown = e => e.keyCode === 13 && onSubmit();

  return (
    <Dialog open={true} aria-labelledby="email-link-gate-title">
      <DialogTitle id="email-link-gate-title">
        <EventLabel name="EmailLinkGateTitle" groupName={GROUP} defaultValue="Get your personal link" />
      </DialogTitle>
      {/* No action buttons once sent, so the content carries the bottom spacing itself. */}
      <DialogContent style={sent ? { paddingBottom: 24 } : undefined}>
        {sent ? (
          <p>
            <EventLabel
              name="EmailLinkGateSentMessage"
              groupName={GROUP}
              defaultValue="If the email address is registered for this event, you will receive an email with your personal link within a few minutes. Please also check your spam folder."
            />
          </p>
        ) : (
          <>
            <p>
              <EventLabel
                name="EmailLinkGateIntro"
                groupName={GROUP}
                defaultValue="Enter the email address you registered with and we will send you your personal link to this page."
              />
            </p>
            <CustomInput
              onChange={e => setEmail(e.target.value)}
              label={inputLabel}
              value={email}
              onKeyDown={onKeyDown}
            />
            {invalid && (
              <p className="text-red-500">
                <EventLabel name="EmailLinkGateInvalidEmail" groupName={GROUP} defaultValue="Please enter a valid email address." />
              </p>
            )}
          </>
        )}
      </DialogContent>
      {!sent && (
        <DialogActions>
          <Button onClick={onSubmit} color="primary" disabled={loading}>
            <EventLabel name="EmailLinkGateSubmit" groupName={GROUP} defaultValue="Send link" />
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
});
